import React, { useEffect, useMemo } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import { AnimatePresence, motion } from 'framer-motion';
import {
  X,
  User,
  Phone,
  Mail,
  Fingerprint,
  CalendarDays,
  MapPin,
  Languages,
  Bike,
  Award,
  FileText,
  ExternalLink,
  Hash,
} from 'lucide-react';

import type { CourierSubmissionView } from 'features/courier-portal/services/courierPortalService';

interface Props {
  open: boolean;
  onClose: () => void;
  submission: CourierSubmissionView;
}

const Row: React.FC<{
  icon: React.ElementType;
  label: string;
  value: React.ReactNode;
}> = ({ icon: Icon, label, value }) => (
  <div className="flex items-start gap-3 py-3 border-b border-gray-100 last:border-b-0">
    <div className="h-9 w-9 rounded-xl bg-gray-50 border border-gray-100 flex items-center justify-center text-gray-500 shrink-0">
      <Icon className="h-4 w-4" />
    </div>
    <div className="flex-1 min-w-0">
      <p className="text-[11px] uppercase tracking-wide text-gray-500 font-semibold">
        {label}
      </p>
      <div className="text-sm font-medium text-gray-900 mt-0.5 break-words">
        {value || <span className="text-gray-400">—</span>}
      </div>
    </div>
  </div>
);

const SectionCard: React.FC<{
  title: string;
  children: React.ReactNode;
}> = ({ title, children }) => (
  <section className="border-s-2 border-[#bf0035] bg-[#bf0035]/[0.035] px-4 py-5">
    <h3 className="text-base font-semibold text-gray-900">{title}</h3>
    <div className="pt-4">{children}</div>
  </section>
);

const getInitials = (name: string): string => {
  const parts = name.trim().split(/\s+/).slice(0, 2);
  return parts.map((p) => p[0]?.toUpperCase() ?? '').join('') || '?';
};

const CourierDetailsDrawer: React.FC<Props> = ({ open, onClose, submission }) => {
  const { t } = useTranslation();

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', onKey);
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = prevOverflow;
    };
  }, [open, onClose]);

  const initials = useMemo(
    () => getInitials(submission.fullName),
    [submission.fullName],
  );

  const closeLabel = String(t('portal.detailsDrawer.close'));
  const dialogLabel = String(t('portal.detailsDrawer.title'));

  if (typeof document === 'undefined') return null;

  return createPortal(
    <AnimatePresence>
      {open ? (
        <motion.div
          initial={{ opacity: 1 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 1 }}
          transition={{ duration: 0.25 }}
          className="fixed inset-0 z-[9999]"
        >
          <button
            type="button"
            aria-label={closeLabel}
            onClick={onClose}
            className="absolute inset-0 cursor-default bg-black/35"
          />

          <motion.aside
            role="dialog"
            aria-modal="true"
            aria-label={dialogLabel}
            initial={{ x: '100%' }}
            animate={{ x: 0 }}
            exit={{ x: '100%' }}
            transition={{ duration: 0.25, ease: 'easeInOut' }}
            className="absolute bottom-0 right-0 top-0 flex w-full max-w-[92vw] flex-col border-s border-gray-200 bg-gray-50 sm:w-1/2"
          >
            <div className="bg-[#bf0035] px-5 pb-6 pt-5 text-white sm:px-6">
              <div className="flex items-start justify-between gap-3">
                <div className="flex items-center gap-3 min-w-0">
                  <div className="flex h-14 w-14 shrink-0 items-center justify-center rounded-xl border border-white/20 bg-white/10 text-lg font-bold">
                    {initials}
                  </div>
                  <div className="min-w-0">
                    <p className="text-[11px] uppercase tracking-wider text-white/70 font-semibold">
                      {t('portal.detailsDrawer.title')}
                    </p>
                    <h2 className="text-lg sm:text-xl font-extrabold leading-tight truncate">
                      {submission.fullName}
                    </h2>
                    <p className="text-xs text-white/80 mt-0.5 font-mono">
                      #{submission.submissionId}
                    </p>
                  </div>
                </div>
                <button
                  type="button"
                  onClick={onClose}
                  aria-label={closeLabel}
                  className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg border border-white/20 bg-white/10 transition-colors hover:bg-white/20"
                >
                  <X className="h-4 w-4" />
                </button>
              </div>

            </div>

            {/* Scrollable body */}
            <div className="flex-1 overflow-y-auto px-4 sm:px-5 py-5 space-y-4">
              <SectionCard
                title={t('portal.detailsDrawer.documents')}
              >
                {submission.documents.length === 0 ? (
                  <p className="text-sm text-gray-500 py-2">
                    {t('portal.detailsDrawer.noDocuments')}
                  </p>
                ) : (
                  submission.documents.map((d) => (
                    <Row
                      key={d.key}
                      icon={FileText}
                      label={t(`onboarding.documents.${d.key}`)}
                      value={
                        d.url ? (
                          <a
                            href={d.url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="inline-flex items-center gap-1.5 text-haat-red-600 hover:underline"
                          >
                            <span className="truncate max-w-[180px]">{d.name}</span>
                            <ExternalLink className="h-3.5 w-3.5 shrink-0" />
                          </a>
                        ) : (
                          <span className="truncate">{d.name}</span>
                        )
                      }
                    />
                  ))
                )}
              </SectionCard>

              <SectionCard title={t('portal.detailsDrawer.personal')}>
                <Row
                  icon={User}
                  label={t('onboarding.personal.fullName')}
                  value={submission.fullName}
                />
                <Row
                  icon={Fingerprint}
                  label={t('onboarding.personal.taxId')}
                  value={<span className="font-mono">{submission.taxId}</span>}
                />
                <Row
                  icon={CalendarDays}
                  label={t('onboarding.personal.dateOfBirth')}
                  value={submission.dateOfBirth}
                />
                <Row
                  icon={Phone}
                  label={t('onboarding.personal.phoneNumber')}
                  value={
                    <a
                      href={`tel:${submission.phoneNumber}`}
                      className="text-haat-red-600 hover:underline font-mono"
                      dir="ltr"
                    >
                      {submission.phoneNumber}
                    </a>
                  }
                />
                <Row
                  icon={Mail}
                  label={t('onboarding.personal.email')}
                  value={
                    <a
                      href={`mailto:${submission.email}`}
                      className="text-haat-red-600 hover:underline"
                    >
                      {submission.email}
                    </a>
                  }
                />
              </SectionCard>

              <SectionCard
                title={t('onboarding.personal.detailsTitle')}
              >
                <Row
                  icon={MapPin}
                  label={t('onboarding.personal.city')}
                  value={submission.city}
                />
                <Row
                  icon={Languages}
                  label={t('portal.detailsDrawer.spokenLanguages')}
                  value={
                    submission.spokenLanguages.length ? (
                      <div className="flex flex-wrap gap-1.5 mt-1">
                        {submission.spokenLanguages.map((lang) => (
                          <span
                            key={lang}
                            className="inline-flex items-center rounded-full bg-haat-red-50 text-haat-red-700 border border-haat-red-100 px-2.5 py-0.5 text-[11px] font-semibold"
                          >
                            {lang}
                          </span>
                        ))}
                      </div>
                    ) : (
                      ''
                    )
                  }
                />
                <Row
                  icon={Bike}
                  label={t('onboarding.vehicle.title')}
                  value={(submission.vehicleTypes &&
                  submission.vehicleTypes.length > 0
                    ? submission.vehicleTypes
                    : [submission.vehicleType]
                  )
                    .filter(Boolean)
                    .map((v) => t(`onboarding.vehicle.${v}`))
                    .join(', ')}
                />
                <Row
                  icon={Award}
                  label={t('onboarding.experience.priorExperience')}
                  value={
                    submission.hasDeliveryExperience
                      ? t('common.yes')
                      : t('onboarding.review.noExperience')
                  }
                />
                {submission.yearsOfExperienceRange ? (
                  <Row
                    icon={Hash}
                    label={t('onboarding.experience.yearsLabel')}
                    value={t(
                      `onboarding.experience.${submission.yearsOfExperienceRange}`,
                    )}
                  />
                ) : null}
              </SectionCard>
            </div>

            {/* Footer */}
            <div className="border-t border-gray-200 bg-white px-5 py-3 flex justify-end">
              <button
                type="button"
                onClick={onClose}
                className="btn-outline inline-flex items-center gap-2"
              >
                <X className="h-4 w-4" />
                {t('portal.detailsDrawer.close')}
              </button>
            </div>
          </motion.aside>
        </motion.div>
      ) : null}
    </AnimatePresence>,
    document.body,
  );
};

export default CourierDetailsDrawer;
